/* eslint-disable max-lines */
import {
  greenScreenVerificationModalOpenAtom,
  proceedWithActionAtom
} from "@design-lab/features/green-screen-verification";
import { cn } from "@design-lab/shared";
import { BACKGROUND_TYPE } from "@design-lab/shared/constants";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import {
  trackGreenScreenInvitationGreenScreenClicked,
  trackGreenScreenInvitationSkipClicked
} from "@design-lab/shared/lib/analytics";
import { Badge, Button } from "@design-lab/shared/ui";
import {
  artboardBackgroundAtom,
  backgroundTogglePopoverOpenAtom,
  mobileDrawerExpandedAtom
} from "@design-lab/widgets/artboard/model/state";
import { useAtom, useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

type VerifyDesignPopoverProps = {
  anchorElement: HTMLElement;
  skipButtonText?: string;
};

const MOBILE_POPOVER_HEIGHT = 160;
const SPACING = 8;
const EDGE_PADDING = 16;

const calculatePosition = (
  rect: DOMRect,
  popoverWidth: number
): { left: number; arrowLeft: number } => {
  let { left } = rect;
  if (left + popoverWidth > window.innerWidth) {
    left = window.innerWidth - popoverWidth - EDGE_PADDING;
  }
  if (left < EDGE_PADDING) {
    left = EDGE_PADDING;
  }

  const toggleCenter = rect.left + rect.width / 2;
  const arrowLeft = toggleCenter - left;

  return { left, arrowLeft };
};

type PopoverArrowProps = {
  arrowLeft: number;
  direction: "up" | "down";
};

function PopoverArrow({ arrowLeft, direction }: PopoverArrowProps) {
  const isUp = direction === "up";
  const arrowStyle = {
    left: `${arrowLeft}px`,
    transform: "translateX(-50%)"
  };

  return (
    <>
      <div
        className={cn(
          "absolute h-0 w-0",
          isUp
            ? "-top-2 border-b-8 border-l-8 border-r-8 border-b-white"
            : "-bottom-2 border-l-8 border-r-8 border-t-8 border-t-white",
          "border-l-transparent border-r-transparent"
        )}
        style={arrowStyle}
        data-testid="verify-design-arrow"
      />
      <div
        className={cn(
          "absolute h-0 w-0",
          isUp
            ? "border-b-background -top-[9px] border-b-[9px]"
            : "border-t-background -bottom-[9px] border-t-[9px]",
          "border-l-[9px] border-r-[9px] border-l-transparent border-r-transparent"
        )}
        style={arrowStyle}
      />
    </>
  );
}

export function VerifyDesignPopover({
  anchorElement,
  skipButtonText = "Skip"
}: VerifyDesignPopoverProps) {
  const { isDesktop } = useResponsive();
  const [isOpen, setIsOpen] = useAtom(greenScreenVerificationModalOpenAtom);
  const setBackgroundType = useSetAtom(artboardBackgroundAtom);
  const setProceedWithAction = useSetAtom(proceedWithActionAtom);
  const [drawerExpanded, setDrawerExpanded] = useAtom(mobileDrawerExpandedAtom);
  const setBackgroundToggleOpen = useSetAtom(backgroundTogglePopoverOpenAtom);
  const [position, setPosition] = useState<{ top: number; left: number; arrowLeft: number } | null>(
    null
  );

  useEffect(() => {
    if (!isOpen || !anchorElement) {
      setPosition(null);
      return;
    }

    const originalZIndex = anchorElement.style.zIndex;
    const originalPosition = anchorElement.style.position;
    anchorElement.style.zIndex = "102";
    anchorElement.style.position = "relative";

    const timers: NodeJS.Timeout[] = [];

    if (!isDesktop) {
      const isDrawerOpen = drawerExpanded;

      if (isDrawerOpen) {
        setDrawerExpanded(false);
      }

      const popoverDelay = isDrawerOpen ? 300 : 0;
      const popoverTimer = setTimeout(() => {
        setBackgroundToggleOpen(true);
      }, popoverDelay);
      timers.push(popoverTimer);

      const positionDelay = isDrawerOpen ? 450 : 150;
      const positionTimer = setTimeout(() => {
        const rect = document
          .querySelector("[data-testid='background-toggle-popover']")
          ?.getBoundingClientRect();
        if (!rect) return;

        const popoverWidth = window.innerWidth - 32;
        const { left, arrowLeft } = calculatePosition(rect, popoverWidth);

        setPosition({
          top: rect.top - MOBILE_POPOVER_HEIGHT - SPACING,
          left,
          arrowLeft
        });
      }, positionDelay);
      timers.push(positionTimer);
    } else {
      const rect = anchorElement.getBoundingClientRect();
      const { left, arrowLeft } = calculatePosition(rect, 320);

      setPosition({
        top: rect.bottom + SPACING,
        left,
        arrowLeft
      });
    }

    // eslint-disable-next-line consistent-return
    return () => {
      timers.forEach(clearTimeout);
      anchorElement.style.zIndex = originalZIndex;
      anchorElement.style.position = originalPosition;
      if (!isDesktop) {
        setBackgroundToggleOpen(false);
      }
    };
  }, [isOpen, anchorElement, isDesktop, setDrawerExpanded, setBackgroundToggleOpen]);

  const handleGreenScreenClick = () => {
    trackGreenScreenInvitationGreenScreenClicked();
    setBackgroundType(BACKGROUND_TYPE.GREEN);
    setIsOpen(false);
  };

  const handleSkipClick = () => {
    trackGreenScreenInvitationSkipClicked();
    setIsOpen(false);
    setProceedWithAction(true);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen || !position) return null;

  return createPortal(
    <>
      <div
        className="bg-foreground/50 fixed inset-0 z-[100]"
        data-testid="verify-design-backdrop"
      />

      <div
        className={cn(
          "bg-background fixed z-[101] space-y-1 rounded-xl px-4 py-2",
          "border border-black/10 shadow-2xl",
          isDesktop ? "w-80" : "w-[calc(100%-2rem)] max-w-sm",
          "animate-in fade-in duration-200"
        )}
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`
        }}
        data-testid="verify-design-popover"
        role="dialog"
        aria-modal="true"
        aria-labelledby="verify-design-title"
      >
        <PopoverArrow arrowLeft={position.arrowLeft} direction={isDesktop ? "up" : "down"} />
        <div className="flex items-center gap-2">
          <Badge className="bg-blue-700 text-xs text-white" size="sm">
            Important
          </Badge>
          <h3 id="verify-design-title" className="text-lg font-semibold">
            Verify Design
          </h3>
        </div>

        <p className="text-sm leading-snug text-gray-600">
          Use the <strong>Green Screen</strong> to make any final edits to the background of your
          image.
        </p>

        <Button
          data-testid="verify-design-green-screen-button"
          onPress={handleGreenScreenClick}
          className="mt-2 w-full bg-blue-700 text-white hover:bg-blue-800"
        >
          Green Screen
        </Button>

        <button
          data-testid="verify-design-skip-button"
          onClick={handleSkipClick}
          className="w-full cursor-pointer rounded text-center text-sm text-blue-700 hover:text-blue-800"
        >
          {skipButtonText}
        </button>
      </div>
    </>,
    document.body
  );
}
